import React, { useState, useEffect, useRef, useCallback } from 'react';
import $ from 'jquery';
import Select from 'react-select';
import type { SingleValue, StylesConfig } from 'react-select';
import RenderInBody from './RenderInBody';
import { nanoid } from 'nanoid';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import * as apiHelper from './Utils';
import './ConversationProjects.css';
import ClickOutSide from './ClickOutSide';

// react-select v5 styles matching ComboBoxField / ConversationOwner
const projectSelectStyles: StylesConfig<Project, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '24px',
    height: '24px',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: '11px',
    borderColor: state.isFocused ? '#62accb' : '#ccc',
    width: '150px',
    '&:hover': {
      borderColor: '#62accb',
    },
  }),
  valueContainer: base => ({
    ...base,
    height: '24px',
    padding: '0 6px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '22px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '2px',
    width: '20px',
    '&:hover': {
      color: '#666',
    },
  }),
  placeholder: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: '#aaa',
  }),
  singleValue: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: '#333',
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    width: '150px',
    zIndex: 99999,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '130px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    lineHeight: '1.8',
    padding: '2px 6px',
    minHeight: 'auto',
    backgroundColor: state.isFocused ? '#62accb' : '#fff',
    color: state.isFocused ? '#fff' : '#666666',
    border: state.isFocused ? '1px solid #eee' : '1px solid transparent',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#62accb',
    },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: '11px',
  }),
};

// Types
interface Project {
  ProjectId: number;
  MilestoneId?: number;
  Name: string;
  ConversationId?: number;
  [key: string]: any;
}

interface ConversationLayer {
  LayerId: number;
  [key: string]: any;
}

interface Conversation {
  ConversationId?: number;
  OwnedById?: number;
  IsEnable?: boolean;
  selectedConfigValueId?: string;
  ConfigValueId?: string;
  ProjectID?: string;
  ProjectId?: string | number | null;
  ProjectName?: string;
  MilestoneId?: number;
  ProjectConversationId?: number;
  AlternateProjectName?: string;
  ConversationLayers?: ConversationLayer[];
  [key: string]: any;
}

interface ApiConfig {
  [key: string]: any;
}

interface ConversationProjectsProps {
  apiConfig: ApiConfig;
  itemType?: number;
  conversation: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  onChangedProject?: (e: any, item: Conversation) => void;
}

const ConversationProjects: React.FC<ConversationProjectsProps> = ({
  apiConfig,
  conversation,
  disableAutoSave = false,
  isNew = false,
  onChangedProject,
}) =>
{
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);

  const [elementId] = useState(() => nanoid(8));
  const [canEdit, setCanEdit] = useState(false);
  const [editorEnabled, setEditorEnabled] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Partial<Project>>({});
  const [selectedProjectName, setSelectedProjectName] = useState('Not Set');
  const [dataProjects, setDataProjects] = useState<Project[]>([]);
  const [configValueId, setConfigValueId] = useState(
    conversation?.selectedConfigValueId || conversation?.ConfigValueId || '',
  );
  const [_isLoading, setIsLoading] = useState(false);
  void _isLoading;
  const [previousProjectId, setPreviousProjectId] = useState<number | string>(0);
  const [posTop, setPosTop] = useState(0);
  const [posLeft, setPosLeft] = useState(0);
  const [CSSPosition, setCSSPosition] = useState<'fixed' | 'absolute'>('fixed');

  // Readjust position
  const readjustPosition = useCallback(() =>
  {
    const fieldContainer = containerRef.current;
    if (fieldContainer)
    {
      const fce = fieldContainer.getBoundingClientRect();
      let newPosLeft = fce.left;
      let newPosTop = fce.top;
      let newCSSPosition: 'fixed' | 'absolute' = 'fixed';

      const conversationDetailsViewBody = fieldContainer.closest('.conversationDetailsViewBody');
      if (conversationDetailsViewBody)
      {
        newPosTop = 20;
        newPosLeft = 0;
        newCSSPosition = 'absolute';
      }

      setPosTop(newPosTop);
      setPosLeft(newPosLeft);
      setCSSPosition(newCSSPosition);
    }
  }, []);

  // Close select
  const onCloseSelect = useCallback(() =>
  {
    setEditorEnabled(false);
  }, []);

  // Get project name by ID
  const getProjectNameById = useCallback(
    (id: number) =>
    {
      for (let i = 0; i < dataProjects.length; i++)
      {
        if (dataProjects[i]!.ProjectId === id || dataProjects[i]!.MilestoneId === id)
        {
          return dataProjects[i]!.Name;
        }
      }
      return undefined;
    },
    [dataProjects],
  );

  // Get conversation ID by project ID
  const getConvIdByProjId = useCallback(
    (id: number) =>
    {
      for (let i = 0; i < dataProjects.length; i++)
      {
        if (dataProjects[i]!.ProjectId === id || dataProjects[i]!.MilestoneId === id)
        {
          return dataProjects[i]!.ConversationId;
        }
      }
      return undefined;
    },
    [dataProjects],
  );

  // Save by blur
  const onSaveByBlur = useCallback(
    (e?: any) =>
    {
      const convItemCopy = { ...conversation };

      if (selectedProject.ProjectId)
      {
        convItemCopy.ProjectId = selectedProject.ProjectId;
        convItemCopy.ProjectName = getProjectNameById(selectedProject.ProjectId) || 'Not Set';
        convItemCopy.AlternateProjectName = convItemCopy.ProjectName;
        convItemCopy.ProjectConversationId = getConvIdByProjId(selectedProject.ProjectId);
      }
      else
      {
        convItemCopy.ProjectId = null;
        convItemCopy.ProjectName = 'Not Set';
        convItemCopy.ConversationLayers = [];
        convItemCopy.AlternateProjectName = convItemCopy.ProjectName;
        convItemCopy.ProjectConversationId = getConvIdByProjId(selectedProject.ProjectId as number);

        const MyPersonLayers = CorgeeStorageHelper.getAllLayers();
        convItemCopy.ConversationLayers = MyPersonLayers.filter((e: any) => e.LayerId === 1);
      }

      if (!disableAutoSave)
      {
        const request = {
          ConversationId: convItemCopy.ConversationId,
          ProjectId: convItemCopy.ProjectId,
          PreviousProject: previousProjectId,
        };

        apiHelper
          .put(apiConfig as any, 'api/conversation/UpdateProject', request, true)
          .then(() =>
          {
            if (onChangedProject)
            {
              onChangedProject(e, convItemCopy);
            }
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
          });
      }
      else
      {
        if (onChangedProject)
        {
          onChangedProject(e, convItemCopy);
        }
      }

      onCloseSelect();
    },
    [
      conversation,
      selectedProject,
      disableAutoSave,
      previousProjectId,
      apiConfig,
      onChangedProject,
      getProjectNameById,
      getConvIdByProjId,
      onCloseSelect,
    ],
  );

  // Select project
  const onSelectProject = useCallback(
    (selectedItem: SingleValue<Project>) =>
    {
      if (selectedItem)
      {
        setSelectedProject(selectedItem);
        setSelectedProjectName(selectedItem.Name);
      }
      else
      {
        setSelectedProject({});
        setSelectedProjectName('Not Set');
      }

      if (isNew)
      {
        // For new tasks, build conversation directly with the selected item
        // to avoid stale closure issue with onSaveByBlur
        const convItemCopy = { ...conversation };
        if (selectedItem)
        {
          convItemCopy.ProjectId = selectedItem.ProjectId;
          convItemCopy.ProjectName = selectedItem.Name;
          convItemCopy.AlternateProjectName = selectedItem.Name;
          convItemCopy.ProjectConversationId = selectedItem.ConversationId;
        }
        else
        {
          convItemCopy.ProjectId = null;
          convItemCopy.ProjectName = 'Not Set';
          convItemCopy.AlternateProjectName = 'Not Set';
          const MyPersonLayers = CorgeeStorageHelper.getAllLayers();
          convItemCopy.ConversationLayers = MyPersonLayers.filter((e: any) => e.LayerId === 1);
        }
        if (onChangedProject)
        {
          onChangedProject(undefined, convItemCopy);
        }
        onCloseSelect();
      }
      else
      {
        setTimeout(() =>
        {
          onSaveByBlur();
        });
        onCloseSelect();
      }
    },
    [isNew, conversation, onChangedProject, onSaveByBlur, onCloseSelect],
  );

  // Apply style
  const onApplyStyle = useCallback(() =>
  {
    if (containerRef.current)
    {
      const $containerElement = $(containerRef.current);
      const $targetElement = $containerElement.find('#select-target-' + elementId);
      const $selectElement = $('#select-render-' + elementId);

      $selectElement
        .find('.Select')
        .first()
        .attr(
          'style',
          `position:fixed; top: ${$targetElement.offset()?.top}px; left: ${$targetElement.offset()?.left}px`,
        );

      $containerElement
        .closest('.layer-custom-fields-container')
        .first()
        .on('scroll', onCloseSelect);
    }
  }, [elementId, onCloseSelect]);

  // Enable editor
  const onEnableEditor = useCallback(
    (e: React.MouseEvent) =>
    {
      if (isNew)
      {
        const cfgId =
          configValueId || conversation?.selectedConfigValueId || conversation?.ConfigValueId || '';

        if (!cfgId)
        {
          console.warn('[ConversationProjects] No configValueId available, cannot fetch projects');
          return;
        }

        setIsLoading(true);

        apiHelper
          .get(apiConfig as any, `api/project/getProjectsByConfigValueId?configValueId=${cfgId}`)
          .then((dataResponse: Project[]) =>
          {
            setDataProjects(dataResponse);
            setIsLoading(false);
            setEditorEnabled(true);
            setTimeout(() =>
            {
              readjustPosition();
              if (selectRef.current)
              {
                selectRef.current.focus();
              }
            });
          })
          .catch(error =>
          {
            console.warn('[ConversationProjects] Api Error', error);
            setIsLoading(false);
          });
      }
      else
      {
        if (onChangedProject)
        {
          onChangedProject(e, conversation);
        }
      }
    },
    [
      isNew,
      canEdit,
      editorEnabled,
      apiConfig,
      configValueId,
      conversation,
      onChangedProject,
      selectedProjectName,
    ],
  );

  // Effect: Component mount and update canEdit
  useEffect(() =>
  {
    if (isNew)
    {
      setCanEdit(true);
      return;
    }

    const isRecycleBin = window.location.href.indexOf('RecycleBin') > -1;
    let canEditValidated = false;
    let isEnable: boolean;

    if (conversation !== undefined)
    {
      isEnable = !conversation.IsEnable && isRecycleBin ? false : true;

      const projectIdValue =
        conversation.MilestoneId || conversation.ProjectId || conversation.ProjectID;
      const newSelectedProject: Partial<Project> = {
        ProjectId: projectIdValue as number,
      };

      if (conversation.ProjectName && conversation.ProjectName.length > 0)
      {
        newSelectedProject.Name = conversation.ProjectName;
        setSelectedProjectName(conversation.ProjectName);
      }

      setSelectedProject(newSelectedProject);
      setPreviousProjectId(projectIdValue as number);
    }
    else
    {
      isEnable = !isRecycleBin;
    }

    canEditValidated = CorgeeStorageHelper.canEdit() && isEnable;

    if (conversation)
    {
      let canEditProject = false;

      if (
        CorgeeStorageHelper.canEdit() &&
        conversation.OwnedById === CorgeeStorageHelper.personId()
      )
      {
        canEditProject = true;
      }
      if (CorgeeStorageHelper.isAdmin())
      {
        canEditProject = true;
      }

      canEditValidated = canEditProject && isEnable;
    }

    setCanEdit(canEditValidated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect: Watch conversation changes
  useEffect(() =>
  {
    if (conversation)
    {
      const projectIdValue =
        conversation.MilestoneId || conversation.ProjectId || conversation.ProjectID;

      setConfigValueId(conversation.selectedConfigValueId || '');

      if (selectedProject.ProjectId !== projectIdValue)
      {
        const newSelectedProject: Partial<Project> = {
          ProjectId: projectIdValue as number,
          Name:
            conversation.ProjectName && conversation.ProjectName.length > 0
              ? conversation.ProjectName
              : 'Not Set',
        };

        setSelectedProject(newSelectedProject);
        setSelectedProjectName(newSelectedProject.Name || 'Not Set');
        setPreviousProjectId(projectIdValue as number);
      }
      else if (
        projectIdValue &&
        conversation.ProjectName &&
        conversation.ProjectName.length > 0 &&
        selectedProjectName === 'Not Set'
      )
      {
        // The project id can be known before its name (the inline card inside a
        // project resolves the name asynchronously): the id hasn't changed, so
        // the branch above skips — refresh the label once the name lands.
        setSelectedProject({ ProjectId: projectIdValue as number, Name: conversation.ProjectName });
        setSelectedProjectName(conversation.ProjectName);
      }

      if (isNew)
      {
        setCanEdit(true);
      }
      else
      {
        const isRecycleBin = window.location.href.indexOf('RecycleBin') > -1;
        const isEnable = !conversation.IsEnable && isRecycleBin ? false : true;
        let canEditProject = false;

        if (
          CorgeeStorageHelper.canEdit() &&
          conversation.OwnedById === CorgeeStorageHelper.personId()
        )
        {
          canEditProject = true;
        }
        if (CorgeeStorageHelper.isAdmin())
        {
          canEditProject = true;
        }

        setCanEdit(canEditProject && isEnable);
      }
    }
  }, [conversation?.ProjectId, conversation?.MilestoneId, conversation?.ProjectName, conversation?.selectedConfigValueId, conversation?.OwnedById]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedValue = dataProjects.find(p => p.ProjectId === selectedProject.ProjectId) || null;

  const styleRender: React.CSSProperties = {
    display: 'block',
    zIndex: 99999,
    top: posTop,
    left: posLeft,
    position: CSSPosition,
  };

  return (
    <div className="click-to-edit" ref={containerRef}>
      {!canEdit && (
        <div className="lbl-non-editable-input" title={selectedProjectName}>
          {selectedProjectName}
        </div>
      )}
      {canEdit &&
        (editorEnabled ? (
          <div id={`select-target-${elementId}`}>
            <RenderInBody>
              <div
                id={`select-render-${elementId}`}
                className="comboBoxRef ConversationOwner-hidden-control-container"
                style={styleRender}
              >
                <ClickOutSide onClickOutSide={onSaveByBlur}>
                  <Select<Project, false>
                    ref={selectRef}
                    value={selectedValue}
                    options={dataProjects}
                    menuIsOpen={true}
                    isSearchable={true}
                    closeMenuOnSelect={true}
                    isClearable={true}
                    autoFocus={true}
                    getOptionLabel={opt => opt.Name}
                    getOptionValue={opt => String(opt.ProjectId)}
                    onChange={onSelectProject}
                    onMenuOpen={onApplyStyle}
                    placeholder="Select a Project..."
                    onBlur={onSaveByBlur}
                    styles={projectSelectStyles}
                  />
                </ClickOutSide>
              </div>
            </RenderInBody>
          </div>
        ) : (
          <div
            className="lbl-editable-input project-changed"
            onClick={onEnableEditor}
            title={selectedProjectName}
          >
            {selectedProjectName}
          </div>
        ))}
    </div>
  );
};

export default ConversationProjects;
